import { useState } from "react";
import Sidebar from "../components/Sidebar";
import { ICONS } from "../constants/icons";
import { auth, database } from "../constants/firebase";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import useMoveTo from "../constants/navigation";
import MobileHeader from "../components/MobileHeader";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { useUser } from "../contexts/UserContext";
import { ref, set } from "firebase/database";

const ChangePassword = () => {
    const navigate = useMoveTo();
    const { logout, refreshUser } = useUser();
    const [formData, setFormData] = useState({
        currentPassword: "",
        newPassword: "",
    });

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showPasswords, setShowPasswords] = useState({
        currentPassword: false,
        newPassword: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const togglePasswordVisibility = (field) => {
        setShowPasswords((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        const { currentPassword, newPassword } = formData;

        if (!currentPassword || !newPassword) {
            setError("Please fill out both fields.");
            return;
        }

        if (newPassword.length < 6) {
            setError("New password must be at least 6 characters.");
            return;
        }

        setIsSubmitting(true);

        try {
            const user = auth.currentUser;
            if (!user || !user.email) {
                setError("User not logged in.");
                setIsSubmitting(false);
                return;
            }

            // Step 1: Reauthenticate the user
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Step 2: Update the password
            await updatePassword(user, newPassword);
            const userRef = ref(database, `Users/${user.uid}/password`);
            await set(userRef, newPassword);
            await refreshUser();

            window.showToast("Password changed successfully!", "success");
            setFormData({ currentPassword: "", newPassword: "" });

            // Optional: navigate back after success
            setTimeout(() => navigate(-1), 1500);
        } catch (err) {
            console.error("Password change error:", err);
            if (err.code === "auth/invalid-credential") {
                setError("The current password is incorrect.");
            } else if (err.code === "auth/weak-password") {
                setError("New password is too weak.");
            } else {
                setError("Failed to change password. Please try again.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            <MobileHeader title="Change Password" isBackBtn={true} />
            <div className="flex">
                <Sidebar />

                <main className="flex-1 h-screen pt-5 pb-20 px-6 overflow-y-auto bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img
                                src={ICONS.arrowBack}
                                className="w-4 h-4 md:w-5 md:h-5 brightness-0 dark:invert"
                                alt="Back"
                            />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
                            Change Password
                        </h2>
                        <div />
                    </div>

                    <div className="w-full flex items-center justify-center">
                        <div className="w-full max-w-md mt-3 md:mt-20">
                            {/* Header */}
                            <div className="text-center mb-8">
                                <p className="text-sm md:text-base text-gray-400">
                                    Update your account password below.
                                </p>
                                {error && <p className="text-red-500 text-sm mt-3">{error}</p>}
                                {success && <p className="text-green-500 text-sm mt-3">{success}</p>}
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Current Password */}
                                <div className="relative">
                                    <label htmlFor="currentPassword" className="sr-only">
                                        Current Password
                                    </label>
                                    <input
                                        id="currentPassword"
                                        name="currentPassword"
                                        type={showPasswords.currentPassword ? "text" : "password"}
                                        value={formData.currentPassword}
                                        onChange={handleChange}
                                        placeholder="Current Password"
                                        className="input-main w-full pr-10 py-3"
                                        required
                                        minLength={6}
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                                        onClick={() => togglePasswordVisibility("currentPassword")}
                                    >
                                        {showPasswords.currentPassword ? (
                                            <FaRegEye size={20} />
                                        ) : (
                                            <FaRegEyeSlash size={20} />
                                        )}
                                    </button>
                                </div>

                                {/* New Password */}
                                <div className="relative">
                                    <label htmlFor="newPassword" className="sr-only">
                                        New Password
                                    </label>
                                    <input
                                        id="newPassword"
                                        name="newPassword"
                                        type={showPasswords.newPassword ? "text" : "password"}
                                        value={formData.newPassword}
                                        onChange={handleChange}
                                        placeholder="New Password"
                                        className="input-main w-full pr-10 py-3"
                                        required
                                        minLength={6}
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                                        onClick={() => togglePasswordVisibility("newPassword")}
                                    >
                                        {showPasswords.newPassword ? (
                                            <FaRegEye size={20} />
                                        ) : (
                                            <FaRegEyeSlash size={20} />
                                        )}
                                    </button>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    className="btn-primary w-full"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Changing Password..." : "Change Password"}
                                </button>
                            </form>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default ChangePassword;
